import { memo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@air/utils-tailwind';

import { Select, type SelectProps } from '~/components/Select/Select';
import { type DefaultChipType } from '~/components/Select/shared/types';
import { isValidEmailAddress, sanitizeEmail } from '~/utils/EmailUtils';
import { MAX_INVITE_EMAILS, parseInviteEmails } from '~/utils/InviteEmailUtils';

// Right-side breathing room between the chip area and the adornment (matches the adornment's
// `right-2` offset so chips never collide with the dropdown trigger).
const ADORNMENT_GAP_PX = 16;

export type InviteEmailSelectOption = Pick<DefaultChipType, 'label' | 'value'>;

export type InviteEmailSelectProps = Pick<
  SelectProps<InviteEmailSelectOption>,
  'className' | 'data-testid' | 'disabled' | 'inputContainerClassName' | 'placeholder' | 'size'
> & {
  emails: string[];
  endAdornment?: ReactNode;
  endAdornmentClassName?: string;
  error?: string;
  errorId?: string;
  id?: string;
  onEmailsChange: (emails: string[]) => void;
  onErrorChange?: (error: string | undefined) => void;
  onInputValueChange?: (inputValue: string) => void;
};

export const InviteEmailSelect = memo(
  ({
    className,
    'data-testid': testId,
    disabled,
    emails,
    endAdornment,
    endAdornmentClassName,
    error,
    errorId,
    id,
    inputContainerClassName,
    onEmailsChange,
    onErrorChange,
    onInputValueChange,
    placeholder = 'Email',
    size = 'extra-large',
  }: InviteEmailSelectProps) => {
    const [emailInputResetKey, setEmailInputResetKey] = useState(0);
    const [inviteEmailInputValue, setInviteEmailInputValue] = useState('');
    const [adornmentWidth, setAdornmentWidth] = useState(0);
    const adornmentObserverRef = useRef<ResizeObserver | null>(null);
    // Holds the latest emails list synchronously so back-to-back chip adds in the same tick
    // see the result of the previous add. Without this, addInviteEmailValue's closure reads
    // a stale `emails` prop until the parent (Formik) re-renders, and a fast chip-add sequence
    // (typed comma immediately followed by a create-button click) overwrites the first chip
    // instead of appending to it. This was visible in CI but not local because CI was just
    // fast enough at running Cypress steps to outpace React's reconciliation.
    const emailsRef = useRef<string[]>(emails);

    useEffect(() => {
      emailsRef.current = emails;
    }, [emails]);

    const selectedInviteEmailOptions = useMemo<InviteEmailSelectOption[]>(
      () => emails.map((email) => ({ label: email, value: email })),
      [emails],
    );
    const hasInviteEmailError = Boolean(error);
    // Reserve only as much right padding as the adornment actually needs so chip width
    // adapts to the selected role label (e.g. "Editor" vs "Commenter").
    const inputContainerStyle = useMemo(
      () => (endAdornment ? { paddingRight: adornmentWidth + ADORNMENT_GAP_PX } : undefined),
      [adornmentWidth, endAdornment],
    );

    // Callback ref over useEffect so the ResizeObserver only re-attaches when the adornment
    // element itself mounts/unmounts, not on every parent re-render that creates a new ReactNode.
    // The observer is held in a ref so the prior instance can be torn down when React calls
    // the ref with null on unmount (React 18 callback refs don't support cleanup returns).
    const adornmentRefCallback = useCallback((node: HTMLDivElement | null) => {
      adornmentObserverRef.current?.disconnect();
      adornmentObserverRef.current = null;

      if (!node) {
        setAdornmentWidth(0);
        return;
      }

      const observer = new ResizeObserver(([entry]) => {
        setAdornmentWidth(entry.contentRect.width);
      });

      observer.observe(node);
      adornmentObserverRef.current = observer;
    }, []);

    useEffect(() => {
      onInputValueChange?.(inviteEmailInputValue);
    }, [inviteEmailInputValue, onInputValueChange]);

    const updateInviteEmails = useCallback(
      (nextEmails: string[]) => {
        // Sync the ref synchronously so subsequent chip adds in the same tick see this value
        // instead of waiting for the parent's re-render to bring it back through `emails`.
        emailsRef.current = nextEmails;
        onEmailsChange(nextEmails);
      },
      [onEmailsChange],
    );

    const addInviteEmailValue = useCallback(
      (value: string) => {
        const parsedNextInviteEmails = parseInviteEmails([...emailsRef.current, value].join(','));

        if (parsedNextInviteEmails.error) {
          onErrorChange?.(parsedNextInviteEmails.error);
          return false;
        }

        updateInviteEmails(parsedNextInviteEmails.emails);
        setInviteEmailInputValue('');
        onErrorChange?.(undefined);
        return true;
      },
      [onErrorChange, updateInviteEmails],
    );

    const handleInviteEmailSelectionChange = useCallback<SelectProps<InviteEmailSelectOption>['onSelectionChange']>(
      ({ chips }) => {
        const parsedNextInviteEmails = parseInviteEmails(chips.map((chip) => chip.value).join(','));

        if (parsedNextInviteEmails.error) {
          onErrorChange?.(parsedNextInviteEmails.error);
          return;
        }

        updateInviteEmails(parsedNextInviteEmails.emails);
        onErrorChange?.(undefined);
      },
      [onErrorChange, updateInviteEmails],
    );

    const handleCreateInviteEmail = useCallback<Required<SelectProps<InviteEmailSelectOption>>['onCreate']>(
      (value) => {
        addInviteEmailValue(value);
      },
      [addInviteEmailValue],
    );

    const handleInviteEmailInputChange = useCallback<Required<SelectProps<InviteEmailSelectOption>>['onInputChange']>(
      (value) => {
        setInviteEmailInputValue(value);

        if (!value.includes(',')) {
          onErrorChange?.(undefined);
          return;
        }

        if (addInviteEmailValue(value)) {
          setEmailInputResetKey((key) => key + 1);
        }
      },
      [addInviteEmailValue, onErrorChange],
    );

    const handleInviteEmailInputBlur = useCallback(
      (value: string) => {
        const trimmedValue = value.trim();

        // Incomplete text stays in the input (preserveInputValueOnBlur). Only commit
        // a finished address so focusing the adjacent role dropdown doesn't wipe it.
        if (!trimmedValue || !isValidEmailAddress(sanitizeEmail(trimmedValue))) {
          return false;
        }

        return addInviteEmailValue(trimmedValue);
      },
      [addInviteEmailValue],
    );

    const allowUniqueInviteEmail = useCallback<Required<SelectProps<InviteEmailSelectOption>>['createOptionFilter']>(
      (value) => {
        const email = sanitizeEmail(value);

        return emails.length < MAX_INVITE_EMAILS && !emails.includes(email) && isValidEmailAddress(email);
      },
      [emails],
    );

    return (
      <div className="relative">
        <Select
          key={emailInputResetKey}
          alwaysShowClearItemButton
          // Auto-focus the inner input on every remount triggered by a successful chip add
          // (emailInputResetKey > 0) so the user's next keystroke lands in the input instead
          // of falling through to the document and scrolling the modal.
          autoFocus={emailInputResetKey > 0}
          className={className}
          inputContainerClassName={inputContainerClassName}
          inputContainerStyle={inputContainerStyle}
          createOptionFilter={allowUniqueInviteEmail}
          createVerb="Invite"
          data-testid={testId}
          disabled={disabled}
          hasError={hasInviteEmailError}
          inputAriaDescribedBy={hasInviteEmailError && errorId ? errorId : undefined}
          inputAriaInvalid={hasInviteEmailError || undefined}
          isSearchable
          inputId={id}
          onCreate={handleCreateInviteEmail}
          onInputBlur={handleInviteEmailInputBlur}
          onInputChange={handleInviteEmailInputChange}
          onSelectionChange={handleInviteEmailSelectionChange}
          preserveInputValueOnBlur
          preventInputShrink
          options={[]}
          selectedOptions={selectedInviteEmailOptions}
          size={size}
          noOptionsText="Enter an email address"
          placeholder={placeholder}
          renderAsInput
        />
        {!!endAdornment && (
          <div
            ref={adornmentRefCallback}
            className={cn('absolute top-1/2 right-2 -translate-y-1/2', endAdornmentClassName)}
          >
            {endAdornment}
          </div>
        )}
      </div>
    );
  },
);

InviteEmailSelect.displayName = 'InviteEmailSelect';
